import * as React from 'react';

import { QuillOptionsStatic, RangeStatic, SelectionChangeHandler } from 'quill';

import { useDebounce } from '~teacher/hook/useDebounceCallback';

import { removeEmbed } from '../../../../src/embedded';
import { mathBlotMatcher, mathInnerBlotMatcher } from './blot/MathBlot';
import Quill from './Quill';

export type QuillTextChangeHandler = (payload: {
  html: string;
  text: string;
}) => void;

// Without changing the tsconfig this is the best import I can do.
// Downside is that it doesn't include any typing information.

// eslint-disable-next-line @typescript-eslint/no-var-requires
const Delta = require('quill-delta');

export type QuillDebugType = 'error' | 'warn' | 'log' | 'info' | false;

export const defaultFormat = [
  'bold',
  'code',
  'embeddedLink',
  'italic',
  'attachment',
  'underline',
  'script',
  'math',
  'link',
];

export const defaultToolBar = [
  ['bold', 'italic', 'underline'],
  [{ script: 'sub' }, { script: 'super' }],
  ['math', 'link'],
];

/**
 * Version 1 underline was a span with the underline class.
 */
const version1UnderlineMatcher = [
  'span.underline',
  (node, delta) =>
    delta.compose(new Delta().retain(delta.length(), { underline: true })),
];

/**
 *  version 1 text editor would insert '\n' instead of '\n '
 */
const spaceAfterNewLine = (s: string) => (s ?? '').replace(`\n`, '\n ');

const defaultDebugType = false;

interface QuillArguments {
  placeholder?: string;
  isReadOnly: boolean;
  editorRef: React.RefObject<HTMLDivElement>;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  initialHtml: string;
  formats?: string[];
  toolbar?: React.RefObject<HTMLDivElement>;
  debug?: string | boolean;
}

const quillInnerHtml = (quill: Quill) => {
  if (!quill) return '';
  const quillHtml = quill.root.innerHTML;
  return removeEmbed(quillHtml);
};

const useQuill = ({
  placeholder,
  isReadOnly,
  editorRef,
  scrollContainerRef,
  initialHtml,
  formats,
  toolbar,
  debug = defaultDebugType,
}: QuillArguments) => {
  const [selection, setSelection] = React.useState<RangeStatic>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [contents, setContents] = React.useState<any>();
  const [quill, setQuill] = React.useState<Quill>();

  const textMatcher = React.useMemo(
    () => [
      Node.TEXT_NODE,
      (node: Text) => {
        const nodeText = node.nodeValue;
        const updatedText = [spaceAfterNewLine].reduce(
          (text, func) => func(text),
          nodeText
        );
        return new Delta([{ insert: updatedText }]);
      },
    ],
    []
  );

  React.useEffect(() => {
    const quillOptions: QuillOptionsStatic = {
      placeholder,
      formats,
      readOnly: isReadOnly,
      scrollingContainer: scrollContainerRef
        ? scrollContainerRef.current
        : undefined,
      bounds: scrollContainerRef ? scrollContainerRef.current : undefined,
      modules: {
        prettylink: true,
        toolbar: toolbar ? toolbar.current : undefined,
        clipboard: {
          matchers: [
            version1UnderlineMatcher,
            textMatcher,
            mathBlotMatcher,
            mathInnerBlotMatcher,
          ],
        },
        keyboard: {
          bindings: {
            tab: {
              key: 9,
              handler: () => {
                // Allow tab out of editor. See https://gist.github.com/dyor/6e6e139253425e0c99a6737490cbe6c5
                return true;
              },
            },
          },
        },
      },
      debug,
    };

    const q = new Quill(editorRef.current, quillOptions);

    setQuill(q);

    if (!contents) {
      q.setContents(contents);
    }

    if (selection) {
      const { index, length } = selection;
      quill.setSelection(index, length);
    }

    if (initialHtml) {
      const delta = q.clipboard.convert(initialHtml);
      q.setContents(delta);
    }
    /**
     * This hook probably needs a rewrite, but adding all the
     * dependency variables to the array makes the Quill Editor
     * not work, so don't.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeholder, formats, debug, toolbar]);

  React.useLayoutEffect(() => {
    if (!quill) return;
    if (isReadOnly) {
      quill.disable();
    }
  }, [quill, isReadOnly, toolbar, editorRef, scrollContainerRef]);

  return {
    selection,
    setSelection,
    contents,
    setContents,
    quill,
    setQuill,
  };
};

interface QuillChangeHandlerArgs {
  quill: Quill;
  maxLength: number;
  setSelection: React.Dispatch<React.SetStateAction<RangeStatic>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setContents: React.Dispatch<any>;
  onQuillTextChange: QuillTextChangeHandler;
}
const useQuillChangeHandler = ({
  quill,
  maxLength,
  setSelection,
  setContents,
  onQuillTextChange,
}: QuillChangeHandlerArgs) => {
  const textSelectionChange: SelectionChangeHandler = React.useCallback(() => {
    const selection = quill.getSelection();
    setSelection(selection);
  }, [quill, setSelection]);

  React.useEffect(() => {
    if (!quill) return;
    quill.on('selection-change', textSelectionChange);
  }, [quill, textSelectionChange]);

  const textChangeCallBack = React.useCallback(
    (delta, oldContents) => {
      if (!quill) return;

      const html = quillInnerHtml(quill);
      const text = quill.getText();
      const length = html.length;

      // If the maxlength is hit, just set the contents back to their old contents and prevent the change
      if (maxLength && maxLength < length) {
        quill.setContents(oldContents);
      } else {
        const range = quill.getSelection();
        setSelection(range);
        setContents(delta);
        if (onQuillTextChange) {
          const payload =
            quill.getLength() > 1 || (text ?? '').trim().length > 0
              ? { html, text }
              : { html: '', text: '' };
          onQuillTextChange(payload);
        }
      }
    },
    [maxLength, quill, setSelection, setContents, onQuillTextChange]
  );

  const debouncedTextChangeCallback = useDebounce({
    callback: textChangeCallBack,
    delay: 400,
  });

  React.useEffect(() => {
    if (!quill) return;
    quill.on('text-change', debouncedTextChangeCallback);

    return () => {
      quill.off('text-change', debouncedTextChangeCallback);
    };
  }, [debouncedTextChangeCallback, quill]);
};

interface QuillSelectionChangeHandlerArguments {
  quill: Quill;
  setSelection: React.Dispatch<React.SetStateAction<RangeStatic>>;
  onSelectionChanged: (
    range: RangeStatic,
    oldrange: RangeStatic,
    source: string
  ) => void;
}

const useQuillSelectionChangeHandler = ({
  quill,
  setSelection,
  onSelectionChanged,
}: QuillSelectionChangeHandlerArguments) => {
  const selectionChange = React.useCallback(
    () =>
      (range: RangeStatic, oldrange: RangeStatic, source: string): void => {
        setSelection(range);
        onSelectionChanged(range, oldrange, source);
      },
    [setSelection, onSelectionChanged]
  );
  React.useLayoutEffect(() => {
    if (!quill) return;
    quill.on('selection-change', selectionChange);
  }, [quill, selectionChange]);
};

export { useQuill, useQuillChangeHandler, useQuillSelectionChangeHandler };
